// Mirrors Finkit.Host.GlobalSymbols contracts
// (GlobalSymbolsAppService + SymbolRequestsAppService).

import type { PagedResult } from '@/api/types/abp';

// Finkit.GlobalSymbols.Enums.SymbolRequestStatus (numeric wire values).
export enum SymbolRequestStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
}

// The instrument type whose SB underlying currency field is enabled.
export const SPREAD_BET = 'Spread Bet';

// Finkit.Host.GlobalSymbols.GlobalSymbolDto (EntityDto<Guid>).
export interface GlobalSymbolDto {
  id: string;
  symbol: string;
  typeId: number;
  typeName: string;
  sbUnderlyingCurrency: string;
  assetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  quoteAssetClass: string;
  countryName: string;
  contractSize: number;
  digits: number;
}

// Finkit.Host.GlobalSymbols.CreateGlobalSymbolDto.
export interface CreateGlobalSymbolDto {
  symbol: string;
  typeId: number;
  sbUnderlyingCurrency: string;
  assetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  quoteAssetClass: string;
  countryName: string;
  contractSize: number;
  digits: number;
}

// Finkit.Host.GlobalSymbols.UpdateGlobalSymbolDto (symbol is NOT updatable).
export interface UpdateGlobalSymbolDto {
  typeId: number;
  sbUnderlyingCurrency: string;
  assetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  quoteAssetClass: string;
  countryName: string;
  contractSize: number;
  digits: number;
}

export interface SymbolTypeItemDto {
  id: number;
  name: string;
}

// Finkit.Host.GlobalSymbols.GlobalSymbolFormDataDto.
export interface GlobalSymbolFormDataDto {
  countryList: string[];
  assetClassList: string[];
  symbolTypeList: SymbolTypeItemDto[];
}

// Finkit.Host.GlobalSymbols.SymbolRequestRowDto (history rows).
export interface SymbolRequestRowDto {
  id: string;
  symbol: string;
  typeId: number;
  typeName: string;
  assetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  contractSize: number;
  digits: number;
  countryName: string;
  clientName: string;
  connectorName: string;
  requestType: string;
  status: string;
  resolvedAt?: string | null;
}

// Finkit.Host.GlobalSymbols.PendingSymbolRequestDto (popup rows).
export interface PendingSymbolRequestDto {
  id: string;
  symbol: string;
  assetClass: string;
  quoteAssetClass: string;
  baseCurrency: string;
  quoteCurrency: string;
  contractSize?: number | null;
  digits?: number | null;
  status: SymbolRequestStatus;
  type: string;
  clientName: string;
  countryName: string;
  typeId: number;
  sbUnderlyingCurrency: string;
  connectorName: string;
  updatedAt?: string | null;
}

// Finkit.Host.GlobalSymbols.ApproveSymbolRequestDto.
export interface ApproveSymbolRequestDto {
  requestId: string;
  globalSymbol: CreateGlobalSymbolDto;
}

// Finkit.Host.GlobalSymbols.BulkApproveResultDto.
export interface BulkApproveResultDto {
  createdSymbols: GlobalSymbolDto[];
  errors: string[];
  approvedCount: number;
}

// GlobalSymbolDatatableInputDto : PaginatedInputDto.
export interface GlobalSymbolDatatableInput {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  draw?: number;
  search?: string;
  symbol?: string;
  typeName?: string;
  sbUnderlyingCurrency?: string;
  assetClass?: string;
  baseCurrency?: string;
  quoteCurrency?: string;
  quoteAssetClass?: string;
  countryName?: string;
  contractSize?: number;
  digits?: number;
}

// SymbolRequestHistoryDatatableInputDto : PaginatedInputDto.
export interface SymbolRequestHistoryDatatableInput {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  draw?: number;
  search?: string;
  symbol?: string;
  typeName?: string;
  assetClass?: string;
  baseCurrency?: string;
  quoteCurrency?: string;
  countryName?: string;
  clientName?: string;
  connectorName?: string;
  requestType?: string;
  status?: string;
  contractSize?: number;
  digits?: number;
  resolvedAt?: string;
}

export type GlobalSymbolPagedResult = PagedResult<GlobalSymbolDto>;
export type SymbolRequestHistoryPagedResult = PagedResult<SymbolRequestRowDto>;
